import { API_URL } from "../utils/constants";
import { IInvestor } from "./investor/investorService";
import {
  fetchWithAuth,
  isNative,
  saveTokens,
  clearTokens,
} from "../utils/auth";
import { ITenant } from "./tenant/tenantService";
import { logoutNotificationService } from "./logoutNotificationService";
import { clearAppReviewData } from "../utils/appReview";
import { errorFromResponse } from "../utils/http-error";

export interface IEmployee {
  employee_id: number;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
  created_at: string;
  updated_at: string;
}

export const checkSession = async (): Promise<void> => {
  const response = await fetchWithAuth(`${API_URL}/auth/`, {
    method: "GET",
  });

  if (!response.ok) throw await errorFromResponse(response);

  return Promise.resolve();
};

export const login = async (
  email: string,
  password: string
): Promise<IInvestor | ITenant> => {
  // For native apps, we need to include the x-native-app header
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (isNative) {
    headers["x-native-app"] = "true";
  }

  const response = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    credentials: isNative ? undefined : "include",
    headers,
    body: JSON.stringify({ email, password }),
  });

  if (response.status !== 200) throw await errorFromResponse(response);

  const data = await response.json();

  {
    // For native apps, store the tokens
    if (isNative && data.accessToken && data.refreshToken) {
      await saveTokens(data.accessToken, data.refreshToken);
      return data.user;
    }
    return data; // Browser/PWA API returns user object directly
  }
};

export const tokenLogin = async (
  token: string,
  targetId: number,
  targetType: string
): Promise<IInvestor | ITenant> => {
  // For native apps, we need to include the x-native-app header
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (isNative) {
    headers["x-native-app"] = "true";
  }

  const response = await fetch(`${API_URL}/auth/token-login`, {
    method: "POST",
    credentials: isNative ? undefined : "include",
    headers,
    body: JSON.stringify({ token, targetId, targetType }),
  });

  if (response.status !== 200) throw await errorFromResponse(response);

  const data = await response.json();

  {
    // For native apps, store the tokens
    if (isNative && data.accessToken && data.refreshToken) {
      await saveTokens(data.accessToken, data.refreshToken);
      return data.user;
    }
    return data; // Browser/PWA API returns user object directly
  }
};

export const logout = async (): Promise<boolean> => {
  const response = await fetchWithAuth(`${API_URL}/auth/logout`, {
    method: "POST",
  });

  // For native apps, clear the tokens
  if (isNative) {
    await clearTokens();
  }

  // Clear app review data to prevent leaking to next user
  clearAppReviewData();

  // Notify other tabs about the logout
  logoutNotificationService.notifyLogout();

  return response.status === 200;
};

export const forgotPassword = async (email: string): Promise<boolean> => {
  const response = await fetchWithAuth(`${API_URL}/auth/forgot-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
    }),
  });

  if (response.status !== 201) throw await errorFromResponse(response);
  return true;
};

/**
 * The address travels with the code now.
 *
 * A code on its own used to identify the account, which meant a guess did not
 * have to name its target. The page already holds the address from the first
 * step, so this costs the person nothing.
 */
export const checkForgotPasswordCode = async (
  email: string,
  code: string
): Promise<boolean> => {
  const response = await fetchWithAuth(
    `${API_URL}/auth/check-reset-password-code`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        code,
      }),
    }
  );

  if (!response.ok) throw await errorFromResponse(response);

  const data = await response.json();
  return data.isValid;
};

export const resetPassword = async (
  email: string,
  code: string,
  password: string
): Promise<boolean> => {
  const response = await fetchWithAuth(`${API_URL}/auth/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      code,
      password,
    }),
  });

  if (!response.ok) throw await errorFromResponse(response);
  return true;
};
